import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { format } from 'date-fns';
import { LookupService } from 'app/core/services/lookup-service';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/essette-api';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { Toaster } from 'app/shared/lib/toaster';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import * as Core from 'app/core/models/essette-api';

const DAYS_OF_WEEK: string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

interface CustomBusinessHour extends Core.BusinessHour {
    startTime: string;
    endTime: string;
}

@autoinject()
export class BusinessHoursPage extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.booleanAttr()
    protected readonly: boolean = false;

    protected validate = validate;
    protected isValid: boolean = false;
    protected nothingChanged: boolean = true;

    // compare active hours with previous Start/End Time selections to determine if any changes were made
    private get detectChanged(): boolean {
        const activeHours = this.businessHours?.filter(h => h.isActive);
        if(this.legacyHours.findIndex(h => activeHours?.find(b => h.dayOfWeek === b.dayOfWeek)) < 0)
            return false;
        if(activeHours && activeHours.findIndex(h => this.legacyHours.find(b => h.dayOfWeek === b.dayOfWeek)?.startTime !== h.startTime) >= 0)
            return false;
        if(activeHours && activeHours.findIndex(h => this.legacyHours.find(b => h.dayOfWeek === b.dayOfWeek)?.endTime !== h.endTime) >= 0)
            return false;
        return true;
    }

    protected businessHours?: ReadonlyArray<CustomBusinessHour>;
    private legacyHours: ReadonlyArray<CustomBusinessHour> = DAYS_OF_WEEK.map(day => { return { isActive: false, dayOfWeek: day, startTime: '', endTime: '' } });

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: LookupService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        await this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadBusinessHours();
    }

    public reset(): void {
        this.businessHours = deepClone(this.legacyHours);
        this.nothingChanged = true;
    }

    public hasChanges(): boolean {
        return !this.nothingChanged;
    }

    protected timeChanged(): void {
        this.nothingChanged = this.detectChanged;
    }

    public async submit(): Promise<boolean> {
        return await this.saveBusinessHours();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadBusinessHours(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true
        };

        const midnight = new DateValue(format(Date.now(), 'yyyy-MM-dd')).toDateTime({ timeZone: this.env.displayTimeZone, hour: 0, minute: 0, second: 0, millisecond: 0 });

        try {
            this.state.beginLoad();

            const response = await this.service.loadBusinessHours(options);

            this.legacyHours.forEach(h => {
                let savedBusinessHours = response.results.filter(r => h.dayOfWeek === r.dayOfWeek);
                if(!savedBusinessHours.length) return;

                let activeHour = savedBusinessHours.length === 1 ? savedBusinessHours[0] : savedBusinessHours.find(h => h.isActive);
                if(!activeHour) {
                    savedBusinessHours = savedBusinessHours.sort((a, b) =>
                        a.metadata?.activeChanged! > b.metadata?.activeChanged! ? 1 :
                            a.metadata?.activeChanged! < b.metadata?.activeChanged! ? -1 :
                                0);
                    activeHour = savedBusinessHours[0];
                }

                if(isNullOrUndefined(activeHour.start?.minutes) || isNullOrUndefined(activeHour.duration?.minutes)) return;
                const displayStartTime = midnight.add(activeHour.start!.minutes, 'minutes');
                const displayEndTime = displayStartTime.add(activeHour.duration!.minutes, 'minutes');
                h.startTime = displayStartTime.toString();
                h.endTime = displayEndTime.toString();
                h.start = { minutes: activeHour.start!.minutes };
                h.duration = { minutes: activeHour.duration!.minutes };
                h.id = activeHour.id;
                h.isActive = activeHour.isActive;
            });
            this.reset();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async activateBusinessHour(businessHour: Core.BusinessHour, isActive: boolean): Promise<void> {
        if(!businessHour.id) {
            businessHour.isActive = isActive;
            this.nothingChanged = this.detectChanged;
            return;
        }

        if(!isActive) {
            const message: string = this.i18n.tr('ui:messages.actions.deactivate', { what: this.i18n.tr('core:businessHour.$') });
            const result = await confirmation.showCustom(message, null, confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            await this.service.activateBusinessHour(businessHour.id, isActive);
            this.toaster.standard.activate('core:businessHour.$', isActive, true);
            await this.reload();
        } catch(error) {
            // if dependency configurations exist, show specific toast; otherwise show the default (de)activation failure toast
            if(error.status.errors) {
                const configurationDependenciesExist = error.status.errors.some((err: any) => err.description.startsWith('Cannot deactivate the last Business Hour'));
                if(configurationDependenciesExist) this.toaster.error().message(this.i18n.tr('core:local.admin.businessHours.existingConfigurationDependencies')).show();
                else this.toaster.standard.activate('core:businessHour.$', isActive, false);
            }
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async saveBusinessHours(): Promise<boolean> {
        if(!this.businessHours) return false;
        const promises: Promise<IApiActionResults>[] = [];

        const midnight = new DateValue(format(Date.now(), 'yyyy-MM-dd')).toDateTime({ timeZone: this.env.displayTimeZone, hour: 0, minute: 0, second: 0, millisecond: 0 });

        this.businessHours
            .filter(h => h.isActive)
            // Create a promise for each active day
            .forEach(businessHour => {
                const legacyHour = this.legacyHours.find(h => h.dayOfWeek == businessHour.dayOfWeek);
                const displayStartTime = new DateTimeValue(businessHour.startTime);
                const displayEndTime = new DateTimeValue(businessHour.endTime);
                const saveHour: Core.BusinessHour = {
                    id: businessHour.id,
                    isActive: businessHour.isActive,
                    dayOfWeek: businessHour.dayOfWeek,
                    start: { minutes: displayStartTime.difference(midnight, 'minutes') },
                    duration: { minutes: displayEndTime.difference(displayStartTime, 'minutes') }
                };

                if(!legacyHour?.isActive || saveHour.start?.minutes !== legacyHour.start?.minutes || saveHour.duration?.minutes !== legacyHour.duration?.minutes) {
                    promises.push(this.service.saveBusinessHour(saveHour));
                }
            });

        if(promises.length === 0) return false;

        try {
            this.state.beginSave();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            const success = responses.every(r => { return r.isSuccessful });
            this.toaster.standard.saved('core:businessHour.$', success, responses.length);

            await this.loadBusinessHours();
        }
        catch(error) {
            this.toaster.standard.saved('core:businessHour.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
        return true;
    }

}
